import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Tarifs = () => {
  const formules = [
    {
      name: 'Starter',
      description: 'Idéal pour tester nos services',
      price: 'Sur devis',
      popular: false,
      features: [
        'Jusqu\'à 100 appels/mois',
        '1 langue au choix',
        'Horaires de bureau (8h-18h)',
        'Accès dashboard basique',
        'Reporting mensuel',
        'Support email',
        'Formation des agents incluse'
      ]
    },
    {
      name: 'Business',
      description: 'La solution complète pour les PME',
      price: 'À partir de 2 500 €/mois',
      popular: true,
      features: [
        'Jusqu\'à 500 appels/mois',
        '2-3 langues',
        'Horaires étendus (8h-20h)',
        'Accès dashboard avancé',
        'Reporting hebdomadaire',
        'Support prioritaire',
        'Chef de projet dédié',
        'Intégration CRM',
        'Scripts personnalisés'
      ]
    },
    {
      name: 'Enterprise',
      description: 'Pour les volumes importants',
      price: 'Sur devis',
      popular: false,
      features: [
        'Volume illimité',
        'Multilingue (8+ langues)',
        'Disponibilité 24/7',
        'Dashboard personnalisé',
        'Reporting temps réel',
        'Account manager dédié',
        'Équipe dédiée',
        'Intégrations sur mesure',
        'SLA garanti',
        'Audit qualité trimestriel'
      ]
    }
  ];

  const services = [
    {
      name: 'Service Client',
      tarif: 'À partir de 12 €/heure',
      description: 'Accueil téléphonique, support multicanal, gestion des réclamations'
    },
    {
      name: 'Call Center B2B',
      tarif: 'À partir de 15 €/heure',
      description: 'Prospection, qualification de leads, prise de RDV'
    },
    {
      name: 'Answering Service',
      tarif: 'À partir de 300 €/mois',
      description: 'Permanence téléphonique 24/7, filtrage d\'appels'
    },
    {
      name: 'Secrétariat Médical',
      tarif: 'À partir de 500 €/mois',
      description: 'Gestion d\'agenda, prise de RDV patients, rappels'
    },
    {
      name: 'BPO',
      tarif: 'Sur devis',
      description: 'Saisie de données, traitement de commandes, back-office'
    },
    {
      name: 'Gestion des tickets',
      tarif: 'À partir de 8 €/ticket',
      description: 'Traitement, catégorisation, escalade'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Tarifs - ZiExter | Prix Call Center, BPO, Secrétariat</title>
        <meta
          name="description"
          content="Découvrez nos tarifs transparents pour call center, BPO et secrétariat virtuel. Formules adaptées aux TPE, PME et grandes entreprises. Devis gratuit."
        />
        <link rel="canonical" href="https://ziexter.com/tarifs" />
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Tarifs ZiExter – Externalisation sans compromis
            </h1>
            <p className="text-xl text-gray-100">
              Des prix compétitifs, une qualité premium, et le bien-être collaborateur comme priorité.
            </p>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Notre promesse : ne choisissez plus entre coût et qualité</h2>
            <p className="text-lg text-gray-700 mb-4">
              Chez ZiExter, nous croyons qu'il est possible d'allier performance, transparence et humain.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              Nos tarifs d'externalisation sont pensés pour maximiser votre satisfaction client tout en garantissant le bien-être de nos équipes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center text-primary font-semibold">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                3x plus abordable que les solutions traditionnelles
              </div>
              <div className="flex items-center text-primary font-semibold">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Transparence totale, sans frais cachés
              </div>
              <div className="flex items-center text-primary font-semibold">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Sans engagement long terme
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos offres tout inclus</h2>
            <p className="section-subtitle">
              Un contrat basé sur la qualité, pas sur la durée. Vous testez, vous jugez, vous restez.
            </p>
          </div>

      
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="card text-center hover-lift animate-fade-in-up">
              <div className="text-5xl mb-4">🎧</div>
              <h3 className="font-heading font-bold text-2xl mb-2">Service client FR</h3>
              <div className="text-4xl font-bold text-primary mb-2">à partir de 9€/h TTC</div>
              <p className="text-gray-600">Agent dédié, manager sur place, backup inclus</p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-100">
              <div className="text-5xl mb-4">🌍</div>
              <h3 className="font-heading font-bold text-2xl mb-2">Service client bilingue</h3>
              <div className="text-4xl font-bold text-primary mb-2">à partir de 10€/h TTC</div>
              <p className="text-gray-600">FR/EN - Reporting journalier & mensuel inclus</p>
            </div>
          </div>

          <div className="text-center mt-12 animate-fade-in-up">
            <Link to="/devis" className="btn btn-primary btn-lg">
              Demander un devis personnalisé
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Comparez ZiExter aux autres solutions</h2>
          </div>

          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="p-4 text-left">Facteurs qualité</th>
                  <th className="p-4 text-center">ZiExter</th>
                  <th className="p-4 text-center">Externalisation classique</th>
                  <th className="p-4 text-center">Recrutement interne</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="p-4 font-medium">Collaborateur dédié</td>
                  <td className="p-4 text-center text-green-600 font-bold">✅ Inclus</td>
                  <td className="p-4 text-center text-orange-600">🔄 Supplément</td>
                  <td className="p-4 text-center text-green-600 font-bold">✅ Oui</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Manager sur place</td>
                  <td className="p-4 text-center text-green-600 font-bold">✅ Inclus</td>
                  <td className="p-4 text-center text-orange-600">🔄 Rarement</td>
                  <td className="p-4 text-center text-red-600 font-bold">❌ Non</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Backup en cas d'absence</td>
                  <td className="p-4 text-center text-green-600 font-bold">✅ Inclus</td>
                  <td className="p-4 text-center text-red-600 font-bold">❌ Non</td>
                  <td className="p-4 text-center text-red-600 font-bold">❌ Non</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Reporting journalier & mensuel</td>
                  <td className="p-4 text-center text-green-600 font-bold">✅ Inclus</td>
                  <td className="p-4 text-center text-orange-600">🔄 En option</td>
                  <td className="p-4 text-center text-red-600 font-bold">❌ Non</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Bien-être collaborateur</td>
                  <td className="p-4 text-center text-green-600 font-bold">🌶️ Priorité</td>
                  <td className="p-4 text-center text-red-600 font-bold">❌ Faible</td>
                  <td className="p-4 text-center text-gray-600">Variable</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Tarif horaire moyen</td>
                  <td className="p-4 text-center text-primary font-bold text-xl">9€/h</td>
                  <td className="p-4 text-center font-bold">13€/h</td>
                  <td className="p-4 text-center font-bold">27€/h</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Bien-être collaborateur */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Un environnement premium pour nos équipes</h2>
            <p className="section-subtitle">
              Chez ZiExter, nous savons qu'un collaborateur heureux = un client satisfait.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🏢', text: 'Espaces lumineux et haut de plafond' },
              { icon: '💪', text: 'Abonnement Salle de sport & espaces détente' },
              { icon: '☕', text: 'Cafés, snacks et repas livrés chaque jour' },
              { icon: '🎁', text: 'Récompenses & cadeaux' },
              { icon: '🎉', text: 'Offsites & événements réguliers' },
              { icon: '🌟', text: 'Bien-être au niveau des startups françaises' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-4xl mb-3">{item.icon}</div>
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Factors */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Qu'est-ce qui influence le tarif ?</h2>
            <p className="section-subtitle">
              Plusieurs critères entrent en compte pour établir votre devis personnalisé
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center animate-fade-in-up">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Volume</h3>
              <p className="text-sm text-gray-600">
                Nombre d'appels, tickets ou heures par mois
              </p>
            </div>

            <div className="text-center animate-fade-in-up animation-delay-100">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Langues</h3>
              <p className="text-sm text-gray-600">
                Nombre et type de langues requises
              </p>
            </div>

            <div className="text-center animate-fade-in-up animation-delay-200">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Horaires</h3>
              <p className="text-sm text-gray-600">
                Bureau, étendus ou 24/7
              </p>
            </div>

            <div className="text-center animate-fade-in-up animation-delay-300">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Complexité</h3>
              <p className="text-sm text-gray-600">
                Scripts, intégrations, formation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils nous font confiance</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="card hover-lift animate-fade-in-up">
              <p className="text-gray-700 italic mb-4">
                "ZiExter nous a aidés à structurer notre service client tout en maîtrisant nos coûts. C'est comme avoir une extension de notre équipe."
              </p>
              <div className="font-semibold">Gautier Marboeuf</div>
              <div className="text-sm text-gray-600">Responsable Support Client</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <p className="text-gray-700 italic mb-4">
                "Transparence, flexibilité et efficacité : c'est rare de trouver les trois réunis, et ZiExter y parvient."
              </p>
              <div className="font-semibold">Clémentine Villedieu</div>
              <div className="text-sm text-gray-600">Responsable Produit</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <p className="text-gray-700 italic mb-4">
                "Un partenaire fiable avec des agents motivés et bien encadrés. La qualité du service est constante et rassurante."
              </p>
              <div className="font-semibold">Éloi Chavanel</div>
              <div className="text-sm text-gray-600">Responsable Relation Client</div>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Pourquoi choisir ZiExter ?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card text-center hover-lift animate-fade-in-up">
              <h3 className="font-semibold mb-2">Sans surprise</h3>
              <p className="text-sm text-gray-600">
                Tarifs simples & transparents. Aucun frais caché.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="font-semibold mb-2">Qualité et rigueur</h3>
              <p className="text-sm text-gray-600">
                Audits qualité, KPI's et management dédié.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="font-semibold mb-2">Innovation & support continu</h3>
              <p className="text-sm text-gray-600">
                Backup intégré, outils digitaux avancés.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-300">
              <h3 className="font-semibold mb-2">Humain avant tout</h3>
              <p className="text-sm text-gray-600">
                Nous investissons dans le bien-être de nos équipes pour booster votre satisfaction client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Construisons ensemble une équipe de service client performante, motivée et rentable
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Réponse sous 24h • Gratuit et sans engagement • Proposition détaillée
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" className="btn btn-accent btn-lg">
                Demandez un devis personnalisé
              </Link>
              <Link to="/rendez-vous" className="btn btn-outline-white btn-lg">
                Planifiez un rendez-vous avec un expert ZiExter
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Tarifs;

